/**
 * The backends a localhost frontend can run against, picked from the Cmd+K
 * menu ("Switch to Prod environment"). `local` is whatever this dev server
 * already proxies /api and /ws to; the remote ones are the deployments.
 *
 * The deployments send no CORS headers, so the browser can't call them from
 * localhost. Instead the app sends its backend traffic to its own origin under
 * `DEV_BACKEND_RELAY_PREFIX` and the frontend dev server relays it
 * (rspack.config.ts). This file is shared by the app (client.ts) and that
 * relay, so it must stay free of browser globals.
 */

export type DevBackendId = 'local' | 'dev' | 'prod';

export interface RemoteDevBackend {
  id: Exclude<DevBackendId, 'local'>;
  label: string;
  origin: string;
}

export const REMOTE_DEV_BACKENDS: readonly RemoteDevBackend[] = [
  { id: 'dev', label: 'Dev', origin: 'https://bot.deepdub.dev' },
  { id: 'prod', label: 'Prod', origin: 'https://bot.deepdub.ai' },
];

/** Relayed requests look like `/__voxcore-backend/prod/api/...`. */
export const DEV_BACKEND_RELAY_PREFIX = '/__voxcore-backend';

/** Response header the relay stamps, so the app can tell it is really there. */
export const DEV_BACKEND_RELAY_HEADER = 'x-voxcore-relay';

export function remoteDevBackend(id: string | null | undefined): RemoteDevBackend | null {
  return REMOTE_DEV_BACKENDS.find((backend) => backend.id === id) ?? null;
}

export function relayPrefix(backend: RemoteDevBackend): string {
  return `${DEV_BACKEND_RELAY_PREFIX}/${backend.id}`;
}

/** Paths the app requests from the Go backend rather than the frontend. The
 *  logo is per-bot branding picked by Referer (tenant_endpoints.go:
 *  handleTenantLogoSVG), despite its /assets path. */
export function isBackendPath(pathname: string): boolean {
  return pathname.startsWith('/api/')
    || pathname === '/ws'
    || pathname.startsWith('/ws/')
    || pathname === '/assets/logo.svg';
}

export function isLoopbackHostname(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  return host === 'localhost'
    || host.endsWith('.localhost')
    || host === '::1'
    || /^127(?:\.\d{1,3}){3}$/.test(host);
}

function isLoopbackUrl(value: string): boolean {
  try {
    return isLoopbackHostname(new URL(value).hostname);
  } catch {
    return false;
  }
}

/**
 * Headers the relay drops so the deployment sees a plain browser request:
 * forwarded-for headers would make it log and rate-limit every relayed caller
 * as 127.0.0.1 (request_actor.go: clientIPFromRequest and main.go:
 * handleWebSocket trust the first X-Forwarded-For), and X-Forwarded-Host
 * would make it build links to localhost. Cookies pass through both ways on purpose: the ALB's
 * AWSALB stickiness cookie keeps a browser on one ECS task
 * (cloudformation/voxcore-alb.yaml), as it does for the deployed frontend.
 */
export const RELAY_DROPPED_REQUEST_HEADERS = [
  'forwarded',
  'x-forwarded-for',
  'x-forwarded-host',
  'x-forwarded-port',
  'x-forwarded-proto',
  'x-real-ip',
] as const;

/**
 * Moves a localhost Origin or Referer onto the deployment's origin (a Referer
 * keeps its path). The voice WS checks a bot's allowed domains against the
 * Referer and auto-registers the first domain it sees (redis_bot_config.go:
 * ValidateBotDomain), so a localhost Referer would lock a real bot to
 * "localhost"; the deployment's own domain is always allowed and never
 * recorded. Anything that isn't a loopback URL passes through.
 */
export function rebaseLoopbackUrl(value: string, origin: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return value;
  }
  if (!isLoopbackHostname(url.hostname)) return value;
  const hasPath = /^[a-z][a-z\d+.-]*:\/\/[^/?#]*[/?#]/i.test(value);
  return hasPath ? origin + url.pathname + url.search + url.hash : origin;
}

/**
 * The upstream path for a relayed request: the prefix comes off, and an
 * `embed_origin` pointing at localhost is dropped (the voice WS prefers it
 * over the Referer for the same domain check). The rest of the query is left
 * byte-for-byte.
 */
export function relayUpstreamPath(path: string, backend: RemoteDevBackend): string {
  const prefix = relayPrefix(backend);
  let rest = path.startsWith(prefix) ? path.slice(prefix.length) : path;
  if (!rest.startsWith('/')) rest = `/${rest}`;
  const queryAt = rest.indexOf('?');
  if (queryAt === -1) return rest;

  const pairs = rest.slice(queryAt + 1).split('&');
  const kept = pairs.filter((pair) => {
    const [rawKey, rawValue = ''] = pair.split('=');
    try {
      const key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
      const value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
      return !(key === 'embed_origin' && isLoopbackUrl(value));
    } catch {
      return true;
    }
  });
  if (kept.length === pairs.length) return rest;
  const pathname = rest.slice(0, queryAt);
  return kept.length > 0 ? `${pathname}?${kept.join('&')}` : pathname;
}
